// Per-connector-type config for the Lead Delivery Engine's Connection/Destination
// forms (LeadDeliverySetup.js) and the release page (LeadDelivery.js). Covers
// form-field shape only — credential fields, connection-level extra fields, and
// destination target_config fields, all rendered as plain label+input pairs.
//
// Discover *results* rendering is deliberately NOT schema-driven here — Convertr's
// nested forms/links and Marketo's flat field list are genuinely different UI
// shapes, kept as explicit branches in LeadDeliverySetup.js's DestinationForm.

export const CONNECTOR_SCHEMAS = {
  convertr: {
    label: 'Convertr',
    credentialFields: [
      { name: 'username', label: 'API Username', type: 'text' },
      { name: 'password', label: 'API Password', type: 'password' },
    ],
    connectionExtraFields: [
      { name: 'publisher_id', label: "Publisher ID (same across this publisher's campaigns)", type: 'number', placeholder: 'e.g. 11176' },
    ],
    destinationFields: [
      { name: 'campaign_id', label: 'Campaign ID', type: 'number', placeholder: 'e.g. 46689' },
      { name: 'form_id', label: 'Form ID', type: 'number' },
      { name: 'link_id', label: 'Link ID (optional)', type: 'number' },
    ],
    mappingPlaceholder: { key: 'form[firstName]', value: 'CSV column (blank = auto-match)' },
    fixedPlaceholder: { key: 'form[assetId]', value: 'literal value' },
    discoverSupported: true,
    discoverNeedsCampaign: true,
    discoverLabel: '🔍 Discover Forms & Links',
    feedbackSupported: true,
  },
  marketo: {
    label: 'Marketo',
    credentialFields: [
      { name: 'identity_endpoint', label: 'Identity Endpoint', type: 'text', placeholder: 'https://123-ABC-456.mktorest.com' },
      { name: 'client_id', label: 'Client ID', type: 'text' },
      { name: 'client_secret', label: 'Client Secret', type: 'password' },
    ],
    connectionExtraFields: [],
    destinationFields: [
      { name: 'lookup_field', label: 'Lookup Field (dedupe key)', type: 'text', placeholder: 'email' },
      { name: 'action', label: 'Action', type: 'text', placeholder: 'createOrUpdate / createOnly / updateOnly' },
      { name: 'program_name', label: 'Program Name (optional — pushes the lead into this program after sync)', type: 'text', placeholder: 'e.g. 26-07 OP - Demand Arc Content Syndication' },
      { name: 'program_status', label: 'Program Status (optional — leave blank for the channel default)', type: 'text', placeholder: 'e.g. Member' },
      { name: 'duplicate_strategy', label: 'If Marketo already has several records with the lead\'s email (optional — blank = the lead fails)', type: 'text', placeholder: 'most_recently_updated / most_recently_created / oldest_created' },
    ],
    mappingPlaceholder: { key: 'email', value: 'CSV column (blank = auto-match)' },
    fixedPlaceholder: { key: 'leadSource', value: 'literal value' },
    discoverSupported: true,
    discoverNeedsCampaign: false,
    discoverLabel: '🔍 Discover Lead Fields',
    // Was correctly false before program-membership status existed as a
    // checkable concept at all — flipped true 2026-08-07 once marketo.py's
    // fetch_feedback() was actually implemented (GET /rest/v1/leads/programs/
    // {programId}.json, hand-verified against a real instance). Only works
    // when the destination has a program_name set — see fetch_feedback()'s
    // own DeliveryError otherwise.
    feedbackSupported: true,
  },
  integrate: {
    label: 'Integrate',
    credentialFields: [
      { name: 'api_key', label: 'API Key', type: 'text' },
      { name: 'api_secret', label: 'API Key Secret', type: 'password' },
    ],
    connectionExtraFields: [],
    destinationFields: [
      { name: 'source_id', label: 'Source ID', type: 'text' },
      // Per-destination rollout gate for the Integrate async callback feature —
      // default off. INTEGRATE_CALLBACK_SECRET/API_BASE_URL are set once per
      // environment (not per destination), so without this checkbox turning the
      // feature on in an environment would activate it for every Integrate
      // destination there at once. See client_delivery_wizard.py's module docstring.
      { name: 'integrate_callback_enabled', label: 'Enable Integrate async callback (beta)', type: 'checkbox' },
    ],
    mappingPlaceholder: { key: 'first_name', value: 'CSV column (blank = auto-match)' },
    fixedPlaceholder: { key: 'lead_source', value: 'literal value' },
    discoverSupported: false,
    discoverNeedsCampaign: false,
    discoverLabel: null,
    feedbackSupported: true,
  },
};

export const schemaFor = (connectorType) => CONNECTOR_SCHEMAS[connectorType] || CONNECTOR_SCHEMAS.convertr;

// One-line summary of a destination's routing config, for the Destinations table
// and the release-page picker — connector-aware instead of assuming campaign_id.
export function destinationSummary(d) {
  const schema = CONNECTOR_SCHEMAS[d.connector_type];
  const cfg = d.effective_target || d.target_config || {};
  if (!schema) return '—';
  const parts = schema.destinationFields
    .map((f) => (cfg[f.name] ? `${f.label.replace(/\s*\(.*/, '')}: ${cfg[f.name]}` : null))
    .filter(Boolean);
  // Marketo per-lead program routing (2026-10-14) — no single Program Name to show
  const routes = cfg.program_routes?.routes;
  if (routes && routes.length) parts.push(`Programs by ${cfg.program_routes.field}: ${routes.length}`);
  return parts.length ? parts.join(' / ') : '—';
}
